import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, Send, CheckCircle2, Loader2, Contact as ContactIcon, Download } from "lucide-react";
import { COMPANY, SERVICES } from "@/data/site";
import { downloadBektechVCard } from "@/lib/vcard";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service_interest: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  const handleSaveContact = async () => {
    setSavingContact(true);
    try {
      await downloadBektechVCard();
      toast.success("Contact card downloaded. Open the .vcf file to add bekim to your contacts.");
    } catch (e) {
      toast.error("Couldn't generate the contact card. Please try again.");
    } finally {
      setSavingContact(false);
    }
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please share your name.";
    if (!form.email.trim()) e.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please share a bit more about your needs (10+ chars).";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleChange = (field) => (ev) => {
    setForm((f) => ({ ...f, [field]: ev.target.value }));
    if (errors[field]) setErrors((er) => ({ ...er, [field]: undefined }));
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, form);
      setSuccess(true);
      toast.success("Request received. We'll be in touch shortly.");
      setForm(initialForm);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again or call us.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-contact">
      {/* HEADER */}
      <section className="relative border-b border-slate-800/80 bg-[#060B15] overflow-hidden">
        <div className="absolute inset-0 bek-grid opacity-40" aria-hidden="true" />
        <div className="absolute inset-0 bek-radial-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32">
          <div className="bek-eyebrow mb-5">Contact</div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white font-bold tracking-tight max-w-4xl">
            Request a <span className="bek-chrome">consultation.</span>
          </h1>
          <p className="mt-6 text-slate-300 max-w-2xl leading-relaxed text-lg">
            Tell us about your environment, your timeline, and what you're trying to accomplish.
            We'll come back with practical next steps.
          </p>
        </div>
      </section>

      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="bg-[#0B1220] border border-slate-800 p-8 sm:p-10">
              {success ? (
                <div data-testid="contact-success" className="text-center py-12">
                  <div className="w-16 h-16 mx-auto grid place-items-center border border-slate-300/40 rounded-full mb-6">
                    <CheckCircle2 className="w-8 h-8 text-slate-300" />
                  </div>
                  <h2 className="text-2xl text-white font-semibold tracking-tight">
                    Thanks — we got it.
                  </h2>
                  <p className="text-slate-400 mt-4 max-w-md mx-auto leading-relaxed">
                    Your consultation request has been received. We'll review the details and respond shortly
                    via email or phone.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSuccess(false)}
                    data-testid="contact-submit-another"
                    className="bek-cta-secondary mt-8"
                  >
                    Submit another request
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} data-testid="consultation-form" className="space-y-6" noValidate>
                  <div className="bek-eyebrow mb-2">Request Consultation</div>
                  <h2 className="text-2xl text-white font-semibold tracking-tight">
                    Tell us about your project
                  </h2>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4">
                    <FormField
                      label="Full Name"
                      required
                      error={errors.name}
                      testid="contact-form-name-input"
                      value={form.name}
                      onChange={handleChange("name")}
                      placeholder="Jane Doe"
                    />
                    <FormField
                      label="Email"
                      required
                      type="email"
                      error={errors.email}
                      testid="contact-form-email-input"
                      value={form.email}
                      onChange={handleChange("email")}
                      placeholder="jane@company.com"
                    />
                    <FormField
                      label="Company"
                      testid="contact-form-company-input"
                      value={form.company}
                      onChange={handleChange("company")}
                      placeholder="Company name (optional)"
                    />
                    <FormField
                      label="Phone"
                      testid="contact-form-phone-input"
                      value={form.phone}
                      onChange={handleChange("phone")}
                      placeholder="(optional)"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-mono-tech tracking-[0.2em] uppercase text-slate-400 mb-2">
                      Service Interest
                    </label>
                    <select
                      data-testid="contact-form-service-select"
                      value={form.service_interest}
                      onChange={handleChange("service_interest")}
                      className="w-full bg-[#080F1C] border border-slate-800 text-slate-100 px-4 py-3 focus:outline-none focus:border-slate-300/60 focus:ring-1 focus:ring-slate-300/40 transition-colors"
                    >
                      <option value="">— Select an area (optional) —</option>
                      {SERVICES.map((s) => (
                        <option key={s.id} value={s.title}>
                          {s.title}
                        </option>
                      ))}
                      <option value="General Inquiry">General Inquiry</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-mono-tech tracking-[0.2em] uppercase text-slate-400 mb-2">
                      Message <span className="bek-chrome">*</span>
                    </label>
                    <textarea
                      data-testid="contact-form-message-input"
                      rows={6}
                      value={form.message}
                      onChange={handleChange("message")}
                      placeholder="Tell us about your environment, project, or audit timeline..."
                      className={`w-full bg-[#080F1C] border ${
                        errors.message ? "border-red-500/60" : "border-slate-800"
                      } text-slate-100 px-4 py-3 focus:outline-none focus:border-slate-300/60 focus:ring-1 focus:ring-slate-300/40 transition-colors resize-y`}
                    />
                    {errors.message && (
                      <p className="mt-2 text-xs text-red-400">{errors.message}</p>
                    )}
                  </div>

                  <div className="pt-2 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <p className="text-xs text-slate-500 font-mono-tech tracking-widest uppercase">
                      We respond within 1 business day.
                    </p>
                    <button
                      type="submit"
                      data-testid="contact-form-submit"
                      disabled={submitting}
                      className="bek-cta disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" /> Sending
                        </>
                      ) : (
                        <>
                          Send Request <Send className="w-4 h-4" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* CONTACT INFO */}
          <div className="lg:col-span-5">
            <div className="border border-slate-800 bg-[#080F1C] p-8 sm:p-10">
              <div className="bek-eyebrow mb-5">Direct Contact</div>
              <h2 className="text-2xl text-white font-semibold tracking-tight">
                Prefer to skip the form?
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed text-sm">
                Reach out by phone or email. We answer most inquiries within one business day.
              </p>

              <div className="mt-8 space-y-5">
                <a
                  href={`mailto:${COMPANY.email}`}
                  data-testid="contact-direct-email"
                  className="flex items-start gap-4 p-4 border border-slate-800 hover:border-slate-300/40 transition-colors group"
                >
                  <Mail className="w-5 h-5 text-slate-300 mt-1" />
                  <div className="min-w-0">
                    <div className="text-[10px] font-mono-tech tracking-[0.25em] uppercase text-slate-500">
                      Email
                    </div>
                    <div className="text-white text-base mt-1 group-hover:text-slate-300 break-all">
                      {COMPANY.email}
                    </div>
                  </div>
                </a>

                <a
                  href={COMPANY.phoneHref}
                  data-testid="contact-direct-phone"
                  className="flex items-start gap-4 p-4 border border-slate-800 hover:border-slate-300/40 transition-colors group"
                >
                  <Phone className="w-5 h-5 text-slate-300 mt-1" />
                  <div>
                    <div className="text-[10px] font-mono-tech tracking-[0.25em] uppercase text-slate-500">
                      Phone
                    </div>
                    <div className="text-white text-base mt-1 group-hover:text-slate-300">
                      {COMPANY.phone}
                    </div>
                  </div>
                </a>

                <div
                  data-testid="contact-direct-engagement"
                  className="flex items-start gap-4 p-4 border border-slate-800"
                >
                  <Send className="w-5 h-5 text-slate-300 mt-1" />
                  <div>
                    <div className="text-[10px] font-mono-tech tracking-[0.25em] uppercase text-slate-500">
                      Engagements
                    </div>
                    <div className="text-white text-base mt-1">On-site & remote</div>
                    <div className="text-xs text-slate-500 mt-1">Project, ongoing, or sprint engagements</div>
                  </div>
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-slate-800">
                <div className="bek-eyebrow mb-3">Save to Your Phone</div>
                <p className="text-sm text-slate-400 leading-relaxed">
                  Download a contact card with our details already filled in — including the bekTech logo — so we're one tap away next time.
                </p>
                <button
                  type="button"
                  onClick={handleSaveContact}
                  disabled={savingContact}
                  data-testid="save-contact-btn"
                  className="mt-5 w-full bek-cta justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingContact ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Preparing
                    </>
                  ) : (
                    <>
                      <ContactIcon className="w-4 h-4" /> Save Contact <Download className="w-4 h-4" />
                    </>
                  )}
                </button>
                <div className="mt-3 text-[11px] font-mono-tech tracking-widest uppercase text-slate-500 text-center">
                  Downloads a .vcf file
                </div>
              </div>

              <div className="mt-10 pt-8 border-t border-slate-800">
                <div className="bek-eyebrow mb-3">Engagement Models</div>
                <ul className="space-y-3 text-sm text-slate-300">
                  <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" /> Project-based consulting</li>
                  <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" /> Ongoing operational support</li>
                  <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" /> Audit-prep & readiness sprints</li>
                  <li className="flex gap-3"><span className="w-1.5 h-1.5 bg-slate-300 rounded-full mt-2" /> Training & knowledge transfer</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function FormField({ label, required, type = "text", error, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-mono-tech tracking-[0.2em] uppercase text-slate-400 mb-2">
        {label} {required && <span className="bek-chrome">*</span>}
      </label>
      <input
        type={type}
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full bg-[#080F1C] border ${
          error ? "border-red-500/60" : "border-slate-800"
        } text-slate-100 px-4 py-3 focus:outline-none focus:border-slate-300/60 focus:ring-1 focus:ring-slate-300/40 transition-colors placeholder:text-slate-600`}
      />
      {error && <p className="mt-2 text-xs text-red-400">{error}</p>}
    </div>
  );
}
